import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  Divider
} from '@mui/material';
import PropTypes from 'prop-types';
import { criarUsuarioPerfil, criarPerfilAcademico } from '../../services/perfilService';

// ⚡ TIPOS_PERFIL expandido com os 7 novos perfis acadêmicos
const TIPOS_PERFIL = [
  // Tipos administrativos existentes
  { value: 'GESTAO', label: 'Gestão', isAcademico: false },
  { value: 'FINANCEIRO', label: 'Financeiro', isAcademico: false },
  { value: 'ACADEMICO', label: 'Acadêmico', isAcademico: false },
  { value: 'ADMIN_INSTITUICAO', label: 'Administrador da Instituição', isAcademico: false },
  
  // ⚡ NOVOS TIPOS ACADÊMICOS
  { value: 'supervisor', label: 'Supervisor', isAcademico: true },
  { value: 'presidente', label: 'Presidente', isAcademico: true },
  { value: 'arguente', label: 'Arguente', isAcademico: true },
  { value: 'revisor_projeto', label: 'Revisor de Projeto', isAcademico: true },
  { value: 'direcao_cientifica', label: 'Direção Científica', isAcademico: true },
  { value: 'direcao_academica', label: 'Direção Acadêmica', isAcademico: true },
  { value: 'chefe_departamento', label: 'Chefe de Departamento', isAcademico: true }
];

// ⚡ CONSTANTES PARA CAMPOS ESPECÍFICOS
const AREAS_ESPECIALIZACAO = [
  'Ciências Exatas',
  'Ciências Humanas', 
  'Ciências Biológicas',
  'Engenharia',
  'Medicina',
  'Direito',
  'Economia',
  'Educação',
  'Tecnologia da Informação',
  'Administração'
];

const DEPARTAMENTOS = [
  'Matemática',
  'Física',
  'Química',
  'Biologia',
  'História',
  'Geografia',
  'Literatura',
  'Filosofia',
  'Engenharia Civil',
  'Engenharia Informática',
  'Medicina',
  'Direito',
  'Economia',
  'Administração'
];

const NIVEIS_ACADEMICOS = [
  { value: 'GRADUACAO', label: 'Graduação' },
  { value: 'ESPECIALIZACAO', label: 'Especialização' },
  { value: 'MESTRADO', label: 'Mestrado' },
  { value: 'DOUTORADO', label: 'Doutorado' },
  { value: 'POS_DOUTORADO', label: 'Pós-Doutorado' }
];

const AdicionarPerfilForm = ({ instituicaoId, onSuccess, onCancel }) => {
  // ⚡ Estado expandido com todos os campos necessários
  const [formData, setFormData] = useState({
    nome: '',
    username: '',
    email: '',
    senha: '',
    confirmSenha: '',
    status: 'ATIVO',
    tipoPerfil: '',
    // Campos específicos para perfis acadêmicos
    areaEspecializacao: '',
    departamento: '',
    areaAtuacao: '',
    nivelAcademico: '',
    experienciaPresidencia: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // ⚡ FUNÇÃO PARA IDENTIFICAR PERFIS ACADÊMICOS
  const isPerfilAcademico = (tipo) => {
    return TIPOS_PERFIL.find(t => t.value === tipo)?.isAcademico || false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const validarFormulario = () => {
    if (!formData.nome) return 'Nome é obrigatório';
    if (!formData.username) return 'Username é obrigatório (apenas letras, números e pontos)';
    if (!formData.tipoPerfil) return 'Tipo de perfil é obrigatório';
    if (!formData.senha) return 'Senha é obrigatória';
    if (formData.senha !== formData.confirmSenha) return 'As senhas não coincidem';
    if (formData.senha.length < 8) return 'A senha deve ter no mínimo 8 caracteres';
    
    // Validação do username (apenas letras, números e pontos)
    const usernameRegex = /^[a-zA-Z0-9.]+$/;
    if (!usernameRegex.test(formData.username)) {
      return 'Username deve conter apenas letras, números e pontos';
    }
    
    return null;
  };

  // ⚡ FUNÇÃO PARA RENDERIZAR CAMPOS ESPECÍFICOS POR TIPO
  const renderCamposEspecificos = () => {
    if (!isPerfilAcademico(formData.tipoPerfil)) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Este tipo de perfil não requer campos adicionais específicos.
        </Alert>
      );
    }

    return (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>
          Campos Específicos para {TIPOS_PERFIL.find(t => t.value === formData.tipoPerfil)?.label}
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Campos específicos baseados no tipo */}
          {(formData.tipoPerfil === 'supervisor' || 
            formData.tipoPerfil === 'arguente' || 
            formData.tipoPerfil === 'revisor_projeto') && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Área de Especialização</InputLabel>
                <Select
                  name="areaEspecializacao"
                  value={formData.areaEspecializacao}
                  onChange={handleChange}
                  label="Área de Especialização"
                >
                  {AREAS_ESPECIALIZACAO.map(area => (
                    <MenuItem key={area} value={area}>{area}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {formData.tipoPerfil === 'presidente' && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Departamento</InputLabel>
                  <Select
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleChange}
                    label="Departamento"
                  >
                    {DEPARTAMENTOS.map(dept => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Experiência em Presidência"
                  name="experienciaPresidencia"
                  value={formData.experienciaPresidencia}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  helperText="Descreva brevemente a experiência"
                />
              </Grid>
            </>
          )}

          {formData.tipoPerfil === 'chefe_departamento' && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Departamento</InputLabel>
                <Select
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleChange}
                  label="Departamento"
                >
                  {DEPARTAMENTOS.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {formData.tipoPerfil === 'direcao_cientifica' && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Departamento</InputLabel>
                  <Select
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleChange}
                    label="Departamento"
                  >
                    {DEPARTAMENTOS.map(dept => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Área de Atuação</InputLabel>
                  <Select
                    name="areaAtuacao"
                    value={formData.areaAtuacao}
                    onChange={handleChange}
                    label="Área de Atuação"
                  >
                    {AREAS_ESPECIALIZACAO.map(area => (
                      <MenuItem key={area} value={area}>{area}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {formData.tipoPerfil === 'direcao_academica' && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Departamento</InputLabel>
                  <Select
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleChange}
                    label="Departamento"
                  >
                    {DEPARTAMENTOS.map(dept => (
                      <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Nível Acadêmico</InputLabel>
                  <Select
                    name="nivelAcademico"
                    value={formData.nivelAcademico}
                    onChange={handleChange}
                    label="Nível Acadêmico"
                  >
                    {NIVEIS_ACADEMICOS.map(nivel => (
                      <MenuItem key={nivel.value} value={nivel.value}>
                        {nivel.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ⚡ DEBUG LOGS
    console.log('🔍 === INÍCIO DO SUBMIT ===');
    console.log('🎯 Tipo selecionado:', formData.tipoPerfil);
    
    const validationError = validarFormulario();
    if (validationError) {
      setError(validationError);
      console.log('❌ Erro de validação:', validationError);
      return;
    }

    setLoading(true);
    try {
      const perfilData = {
        nome: formData.nome,
        username: formData.username,
        email: formData.email,
        senha: formData.senha,
        status: formData.status,
        instituicaoId,
        tipo: formData.tipoPerfil,
        areaEspecializacao: formData.areaEspecializacao,
        departamento: formData.departamento,
        areaAtuacao: formData.areaAtuacao,
        nivelAcademico: formData.nivelAcademico,
        experienciaPresidencia: formData.experienciaPresidencia
      };

      // ⚡ LÓGICA INTELIGENTE: Verificar se é perfil acadêmico
      const tiposAcademicos = [
        'supervisor', 'presidente', 'arguente', 
        'revisor_projeto', 'direcao_cientifica', 
        'direcao_academica', 'chefe_departamento'
      ];
      const isAcademico = tiposAcademicos.includes(formData.tipoPerfil);

      // ⚡ DEBUG LOGS DETALHADOS
      console.log('📊 Dados do perfil:', perfilData);
      console.log('🔬 É perfil acadêmico?', isAcademico);
      console.log('📡 Endpoint que será usado:', isAcademico ? '/auth/registro-admin-institucional' : '/perfis-usuario/novo');

      if (isAcademico) {
        console.log('✅ Chamando criarPerfilAcademico...');
        await criarPerfilAcademico(perfilData);
        console.log('🎉 criarPerfilAcademico executado com sucesso!');
      } else {
        console.log('⚠️ Chamando criarUsuarioPerfil...');
        await criarUsuarioPerfil(perfilData);
        console.log('🎉 criarUsuarioPerfil executado com sucesso!');
      }

      console.log('✅ === SUBMIT CONCLUÍDO COM SUCESSO ===');
      onSuccess();
      
    } catch (err) {
      console.error('❌ === ERRO NO SUBMIT ===');
      console.error('🔥 Erro capturado:', err);
      console.error('📋 Detalhes do erro:', err.response?.data || err.message);
      setError(err.message || 'Erro ao criar perfil');
    } finally {
      setLoading(false);
      console.log('🏁 === FIM DO SUBMIT ===');
    }
  };

  return (
    <>
      <DialogTitle>
        Adicionar Novo Perfil
        {formData.tipoPerfil && (
          <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 1 }}>
            Tipo: {TIPOS_PERFIL.find(t => t.value === formData.tipoPerfil)?.label}
            {isPerfilAcademico(formData.tipoPerfil) && ' (Perfil Acadêmico)'}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Campos básicos */}
          <TextField
            fullWidth
            label="Nome Completo"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            margin="normal"
            required
            helperText="Use apenas letras, números e pontos (sem underscore)"
          />

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
          />

          {/* Tipo de perfil */}
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Tipo de Perfil</InputLabel>
            <Select
              name="tipoPerfil"
              value={formData.tipoPerfil}
              onChange={handleChange}
              label="Tipo de Perfil"
            >
              {TIPOS_PERFIL.map(tipo => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                  {tipo.isAcademico && ' (Acadêmico)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Campos específicos baseados no tipo */}
          {formData.tipoPerfil && renderCamposEspecificos()}

          {/* Campos de senha */}
          <TextField
            fullWidth
            label="Senha"
            name="senha"
            type="password"
            value={formData.senha}
            onChange={handleChange}
            margin="normal"
            required
            helperText="Mínimo de 8 caracteres"
          />

          <TextField
            fullWidth
            label="Confirmar Senha"
            name="confirmSenha"
            type="password"
            value={formData.confirmSenha}
            onChange={handleChange}
            margin="normal"
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Criar Perfil'}
        </Button>
      </DialogActions>
    </>
  );
};

AdicionarPerfilForm.propTypes = {
  instituicaoId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default AdicionarPerfilForm;